// Error handling utilities for AWS Cognito authentication
// Maps technical Cognito error codes to user-friendly messages

export interface CognitoError {
  code: string;
  message: string;
  name?: string;
}

// Mapping of Cognito error codes to user-friendly messages
export const errorMessages: Record<string, string> = {
  // Authentication errors
  'UserNotFoundException': 'No account found with this email address.',
  'NotAuthorizedException': 'Incorrect email or password.',
  'UserNotConfirmedException': 'Please check your email and verify your account before logging in.',
  'PasswordResetRequiredException': 'Password reset is required. Please reset your password.',
  'UserLambdaValidationException': 'User validation failed. Please check your information.',
  'TooManyRequestsException': 'Too many requests. Please wait a moment and try again.',
  
  // Registration errors
  'UsernameExistsException': 'An account with this email already exists.',
  'InvalidPasswordException': 'Password does not meet the required criteria.',
  'InvalidParameterException': 'Invalid input provided. Please check your information and ensure your Cognito User Pool is configured correctly.',
  'CodeMismatchException': 'Invalid verification code. Please check and try again.',
  'ExpiredCodeException': 'Verification code has expired. Please request a new one.',
  'LimitExceededException': 'Attempt limit exceeded. Please try again later.',
  'InvalidEmailRoleAccessPolicyException': 'Email configuration error in Cognito User Pool.',
  'InvalidSmsRoleAccessPolicyException': 'SMS configuration error in Cognito User Pool.',
  'InvalidLambdaResponseException': 'Lambda trigger configuration error in Cognito User Pool.',
  'UnexpectedLambdaException': 'Lambda trigger error in Cognito User Pool.',
  
  // Password reset errors
  'CodeDeliveryFailureException': 'Failed to send verification code. Please try again.',
  'InvalidNewPasswordException': 'New password does not meet requirements.',
  
  // Configuration errors
  'ResourceNotFoundException': 'Cognito configuration error. Please check your User Pool settings.',
  'InvalidUserPoolConfigurationException': 'User Pool configuration error. Please check your Cognito settings.',
  
  // Network and service errors
  'NetworkError': 'Connection problem. Please check your internet connection and try again.',
  'ServiceUnavailableException': 'Service temporarily unavailable. Please try again later.',
  'InternalErrorException': 'An internal error occurred. Please try again.',
  
  // Generic fallback
  'UnknownError': 'An unexpected error occurred. Please try again or contact support.'
};

// Password policy requirements message
export const passwordRequirements = 
  'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.';

/**
 * Maps a Cognito error to a user-friendly message
 * @param error - The error object from Cognito
 * @returns User-friendly error message
 */
export const mapCognitoError = (error: any): string => {
  // Handle different error object structures
  let errorCode: string;
  
  if (typeof error === 'string') {
    errorCode = error;
  } else if (error?.code) {
    errorCode = error.code;
  } else if (error?.name) {
    errorCode = error.name;
  } else if (error?.message) {
    // Try to extract error code from message
    const codeMatch = error.message.match(/([A-Z][a-zA-Z]+Exception)/);
    errorCode = codeMatch ? codeMatch[1] : 'UnknownError';
  } else {
    errorCode = 'UnknownError';
  }
  
  // Return mapped message or fallback
  return errorMessages[errorCode] || errorMessages['UnknownError'];
};

/**
 * Checks if an error is related to password policy
 * @param error - The error object from Cognito
 * @returns True if error is password-related
 */
export const isPasswordPolicyError = (error: any): boolean => {
  const errorCode = error?.code || error?.name || '';
  return errorCode === 'InvalidPasswordException';
};

/**
 * Checks if an error is related to network connectivity
 * @param error - The error object from Cognito
 * @returns True if error is network-related
 */
export const isNetworkError = (error: any): boolean => {
  const errorCode = error?.code || error?.name || '';
  return ['NetworkError', 'ServiceUnavailableException', 'InternalErrorException'].includes(errorCode);
};

/**
 * Checks if an error indicates the user needs to verify their email
 * @param error - The error object from Cognito
 * @returns True if user needs email verification
 */
export const isEmailVerificationRequired = (error: any): boolean => {
  const errorCode = error?.code || error?.name || '';
  return errorCode === 'UserNotConfirmedException';
};

/**
 * Logs error details for debugging (without exposing sensitive information)
 * @param error - The error object from Cognito
 * @param context - Additional context about where the error occurred
 */
export const logError = (error: any, context: string = ''): void => {
  const errorCode = error?.code || error?.name || 'UnknownError';
  const errorMessage = error?.message || 'No message available';
  
  console.error(`[Auth Error] ${context}:`, {
    code: errorCode,
    message: errorMessage,
    timestamp: new Date().toISOString()
  });
};

/**
 * Creates a standardized error object for the application
 * @param error - The original error from Cognito
 * @param context - Context about where the error occurred
 * @returns Standardized error object
 */
export const createAuthError = (error: any, context: string = ''): CognitoError => {
  const errorCode = error?.code || error?.name || 'UnknownError';
  const userMessage = mapCognitoError(error);
  
  // Enhanced logging for debugging
  if (process.env.NODE_ENV === 'development') {
    console.error(`[Auth Error] ${context}:`, {
      code: errorCode,
      message: error?.message || 'No message available',
      originalError: error,
      errorString: JSON.stringify(error, null, 2),
      timestamp: new Date().toISOString()
    });
    
    // Log the full error object structure
    console.error('Full error object:', error);
    console.error('Error keys:', Object.keys(error || {}));
  } else {
    // Log for debugging
    logError(error, context);
  }
  
  return {
    code: errorCode,
    message: userMessage,
    name: context
  };
};